import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import "./styles/exaustores.css";
import AppLayout from "../../layouts/AppLayout";
import FeatureControlBar from "../../components/FeatureControlBar/FeatureControlBar";
import useAuth from "../../hooks/useAuth";
import useDadosCondominio from "../../hooks/useDadosCondominio";
import useActiveCondominio from "../../hooks/useActiveCondominio";
import useExaustorStatus from "./hooks/useExaustorStatus";
import useOperationProgress from "./hooks/useOperationProgress";
import ErrorPopup from "../../components/ErrorPopup/ErrorPopup";
import { useToast } from "../../components/Toast/ToastProvider";
import StatusPill from "../../components/feedback/StatusPill";
import InlineNotice from "../../components/feedback/InlineNotice";
import { FiAlertCircle, FiAlertTriangle } from "react-icons/fi";
import FloatingActionButton from "./components/FloatingActionButton";
import ExaustorStatusCard from "./components/ExaustorStatusCard";
import HistoricoSection from "./components/HistoricoSection";
import ComandoDialog from "./components/ComandoDialog";
import {
  COMANDO_REQUEST_TIMEOUT_MS,
  classificarErroComando,
  descreverFalhaComando
} from "./utils/erroComando";

// Tempo que o resultado de sucesso fica visível antes de o diálogo fechar
const SUCESSO_AUTO_CLOSE_MS = 2000;

const horaMinuto = (data) => data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const App4 = () => {
  const { user } = useAuth();
  const { activeCondominio, setActiveCondominio, condominios } = useActiveCondominio();
  
  // Hook global para dados de condomínio (blocos + apartamentos)
  const { blocos, apartamentos, exaustoresPorBloco, error: errorDados } = useDadosCondominio({ 
    projection: 'exaustores.v1',
  });

  // Estados locais (formulário + UI)
  const [selectedBloco, setSelectedBloco] = useState("");
  const [selectedApto, setSelectedApto] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [userName, setUserName] = useState("");
  const [error, setError] = useState(null);
  const [selectedBlocoStatus, setSelectedBlocoStatus] = useState(""); // Bloco para visualização de status
  const [isSpinningHistorico, setIsSpinningHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [showComandoDialog, setShowComandoDialog] = useState(false);
  // Fases do diálogo de comando: 'form' -> 'sending' -> 'result'. Durante o
  // envio (com os retries acontecendo por trás) só há feedback visual, sem
  // botões: o primeiro pacote já foi publicado, não há o que cancelar.
  const [comandoPhase, setComandoPhase] = useState('form');
  const [resultadoComando, setResultadoComando] = useState(null);
  const [formError, setFormError] = useState(null);

  // Timer do fechamento automatico apos sucesso: guardado para poder ser
  // cancelado se o usuario iniciar outro fluxo ou sair da pagina antes.
  const successTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  const toast = useToast();
  const { progress: sendProgress, start: startSendProgress, stop: stopSendProgress } = useOperationProgress();

  // Hook customizado para gerenciar status dos exaustores (polling + AbortController + refs)
  const {
    status,
    remainingTimes,
    statusError,
    isSpinningStatus,
    isLoadingStatus,
    isRetryingStatus,
    isStatusParcial,
    finaisSemLeitura,
    resetStatus,
    getStatus,
  } = useExaustorStatus(selectedBlocoStatus, activeCondominio);

  // Hora da última leitura recebida, para a StatusPill ("Atualizado às HH:MM").
  // O hook só troca `status` quando chega uma resposta (ou no reset, vazio).
  const [statusAtualizadoEm, setStatusAtualizadoEm] = useState(null);
  useEffect(() => {
    if (Object.keys(status).length > 0) {
      setStatusAtualizadoEm(new Date());
    }
  }, [status]);

  const apiUrl = process.env.REACT_APP_API_URL
  const urlHistorico = `${apiUrl}/api/exaustores/historico`;
  
  const getExaustor = (bloco, apto) => {
    const lastDigit = apto.slice(-1);
    return `exaustor${lastDigit}`;
  };

  // Nome exibido do exaustor que atende o apartamento (o rótulo do grupo,
  // não o número do dispositivo)
  const nomeDoExaustor = (blocoId, apto) => {
    const final = String(apto).slice(-1);
    const grupo = (exaustoresPorBloco[blocoId] || []).find((g) => g.finais.map(String).includes(final));
    return grupo ? `Exaustor ${grupo.exaustor}` : 'Exaustor';
  };

  const limparFormulario = () => {
    setSelectedBloco("");
    setSelectedApto("");
    setSelectedAction("");
    setUserName("");
    setFormError(null);
  };

  const openComandoDialog = () => {
    clearTimeout(successTimerRef.current);
    setComandoPhase('form');
    setResultadoComando(null);
    setShowComandoDialog(true);
  };

  // Fechar pelo × do formulário: descarta o que foi preenchido
  const closeComandoDialog = () => {
    setShowComandoDialog(false);
    limparFormulario();
  };

  // Fechar depois de uma falha (Esc ou "Ver status"): mantém os dados
  // preenchidos, para permitir reenviar sem preencher tudo de novo
  const closeResultado = () => {
    stopSendProgress();
    setShowComandoDialog(false);
    setComandoPhase('form');
    setResultadoComando(null);
  };

  // "Voltar": retorna ao formulário com os dados preservados
  const backToForm = () => {
    stopSendProgress();
    setComandoPhase('form');
    setResultadoComando(null);
  };

  // "Ver status" (resultado incerto): o refresh de status já foi disparado
  // ao receber a falha; aqui só garante que a tela mostre o bloco do comando
  const viewStatus = () => {
    closeResultado();
    setSelectedBlocoStatus(selectedBloco);
  };

  const submitComando = () => {
    if (!selectedBloco || !selectedApto || !selectedAction || !userName) {
      setFormError("Todos os campos são obrigatórios.");
      return;
    }

    const exaustor = getExaustor(selectedBloco, selectedApto);
    if (!exaustor) {
      setFormError("Não foi possível determinar o exaustor para o Bloco/Torre e Apartamento selecionados.");
      return;
    }

    setFormError(null);
    sendCommand();
  };

  // Enviar comando (usado tanto pelo envio do formulário quanto por "Tentar
  // de novo"/"Enviar de novo" após uma falha).
  const sendCommand = async () => {
    clearTimeout(successTimerRef.current);
    setComandoPhase('sending');
    setResultadoComando(null);

    const exaustor = getExaustor(selectedBloco, selectedApto);
    const exaustorNome = nomeDoExaustor(selectedBloco, selectedApto);
    const ligado = selectedAction === 'on';
    const operationId = startSendProgress(apiUrl, activeCondominio);

    const actionData = {
      apto: selectedApto,
      blocoId: selectedBloco,  // Já é INT, sem conversão
      condominioId: activeCondominio,  // 🔥 CRÍTICO: Passar condomínio explicitamente
      pessoa: userName,
      acao: selectedAction,
      dispositivo: exaustor,
      operationId,
    };

    try {
      const response = await axios.post(`${apiUrl}/api/exaustores/comandos`, actionData, {
        headers: {
          'x-condominio-id': activeCondominio
        },
        timeout: COMANDO_REQUEST_TIMEOUT_MS,
        // 429 já aparece no próprio diálogo ("Aguarde um instante")
        skipRateLimitToast: true
      });

      stopSendProgress();
      const dados = response.data.dados || {};
      setResultadoComando(dados);
      setComandoPhase('result');

      if (dados.verificado === true) {
        const confirmadoEm = new Date();

        // Fecha o diálogo e limpa o formulário depois que o usuário viu a
        // confirmação; o toast fica como registro depois que o diálogo some
        successTimerRef.current = setTimeout(() => {
          setShowComandoDialog(false);
          setComandoPhase('form');
          setResultadoComando(null);
          limparFormulario();

          // Atualiza a data selecionada para o dia atual
          const currentTime = new Date();
          const formattedDate = currentTime.toLocaleString().split(",")[0];
          const [day, month, year] = formattedDate.split('/');
          setSelectedDate(`${year}-${month}-${day}`);
          // useEffect dispara fetchHistorico automaticamente quando selectedDate muda

          // Atualiza o bloco selecionado no quadrante de status e busca o status
          setSelectedBlocoStatus(selectedBloco);
          getStatus(selectedBloco);

          toast.success(`${exaustorNome} ${ligado ? 'ligado' : 'desligado'}`, {
            description: `Comando confirmado às ${horaMinuto(confirmadoEm)}.`
          });
        }, SUCESSO_AUTO_CLOSE_MS);
      } else {
        // O status real do exaustor pode já refletir o que aconteceu mesmo
        // com a verificação falhando — atualiza para o operador conferir.
        getStatus(selectedBloco);
      }
    } catch (err) {
      stopSendProgress();

      const falha = classificarErroComando(err);
      setResultadoComando(falha);
      setComandoPhase('result');

      // Bloqueado (409/429): o comando nem foi aceito, atualizar o status so
      // adicionaria carga; nos demais casos ele pode ter sido executado.
      if (descreverFalhaComando(falha).classe !== 'bloqueado') {
        getStatus(selectedBloco);
      }
    }
  };

  const handleBlocoChangeStatus = (blocoId) => {
    setSelectedBlocoStatus(blocoId);
    resetStatus(); // Usa função do hook para resetar status
  };

  const handleBlocoChange = (blocoId) => {
    setSelectedBloco(blocoId);  // Já recebe INT do componente
    setSelectedApto(""); // Limpa o apto quando troca o bloco
    setFormError(null);
  };

  const handleRefresh = () => {
    if (selectedBlocoStatus) {
      resetStatus(); // Reseta antes de buscar novo status
      getStatus(selectedBlocoStatus);
    }
  };

  // Sincronizar erros do hook global
  useEffect(() => {
    if (errorDados) {
      setError(errorDados);
    }
  }, [errorDados]);

  const fetchHistorico = useCallback(async (date) => {
    try {
      // 🔥 CRÍTICO: Passar condominioId para evitar vazamento multi-tenant
      const response = await axios.get(urlHistorico, {
        headers: {
          'x-condominio-id': activeCondominio
        },
        params: { 
          date
        }
      });
      setHistorico(response.data.dados?.historico || []);
    } catch (error) {
      // Se for erro de rate limit, o interceptor já mostrou o alerta
      if (!error.isRateLimitError) {
        console.error("Erro ao buscar histórico de ações:", error);
      }
    }
  }, [urlHistorico, activeCondominio]);

  useEffect(() => {
    if (selectedDate && activeCondominio) {
      fetchHistorico(selectedDate);
    }
  }, [selectedDate, activeCondominio, fetchHistorico]);

  const handleRefreshHistorico = () => {
    const currentTime = new Date();
    const formattedDate = currentTime.toLocaleString().split(",")[0];
    const [day, month, year] = formattedDate.split('/');
    const dataFinal = (`${year}-${month}-${day}`);
    setIsSpinningHistorico(true);
    setSelectedDate(dataFinal);
    // useEffect dispara fetchHistorico automaticamente quando selectedDate muda
    setTimeout(() => setIsSpinningHistorico(false), 300);
  };

  const handleCondomínioChange = (novoCondominioId) => {
    // Atualizar contexto global (hook useDadosCondominio reage automaticamente)
    setActiveCondominio(novoCondominioId);
    
    // Reset apenas UI local específica de exaustores
    setSelectedBloco("");
    setSelectedBlocoStatus("");
    setHistorico([]);
    resetStatus(); // Usa função do hook para resetar status
  };

  // Seleção automática: primeira torre + data atual quando blocos carregam
  useEffect(() => {
    if (blocos && blocos.length > 0 && !selectedBlocoStatus) {
      // Seleciona automaticamente a primeira torre
      const primeiroBloco = blocos[0].id;
      setSelectedBlocoStatus(primeiroBloco);
    }
  }, [blocos, selectedBlocoStatus]);

  // Seleção automática da data atual quando a página carrega
  useEffect(() => {
    if (!selectedDate) {
      const currentTime = new Date();
      const formattedDate = currentTime.toLocaleString().split(",")[0];
      const [day, month, year] = formattedDate.split('/');
      const dataFinal = (`${year}-${month}-${day}`);
      setSelectedDate(dataFinal);
    }
  }, [selectedDate]);

  const exaustoresDoBlocoSelecionado = selectedBlocoStatus
    ? (exaustoresPorBloco[selectedBlocoStatus] || [])
    : [];

  const statusFalhou = statusError === selectedBlocoStatus;

  let statusPillVariant = 'idle';
  if (isRetryingStatus) statusPillVariant = 'retrying';
  else if (isLoadingStatus) statusPillVariant = 'loading';
  else if (statusFalhou) statusPillVariant = 'error';
  else if (isStatusParcial) statusPillVariant = 'partial';

  const nomeBlocoStatus = blocos.find((b) => String(b.id) === String(selectedBlocoStatus))?.nome;

  return (
    <AppLayout
      titulo="Controle de Exaustores"
      cargo={user?.cargo}
      condominios={condominios}
      activeCondominio={activeCondominio}
      onCondomínioChange={handleCondomínioChange}
      problemas={[]}
    >
      <div className="page-container">
        <ErrorPopup error={error} onClose={() => setError(null)} />
        
        {/* Section: Status dos Exaustores */}
        <section className="feature-section">
          <FeatureControlBar
            title="Status dos Exaustores"
            blocos={blocos}
            selectedBloco={selectedBlocoStatus}
            onBlocoChange={handleBlocoChangeStatus}
            onRefresh={handleRefresh}
            isRefreshing={isSpinningStatus}
            titleAddon={selectedBlocoStatus
              ? <StatusPill variant={statusPillVariant} updatedAt={statusAtualizadoEm} />
              : null}
          />

          {statusPillVariant === 'error' && (
            <InlineNotice
              tone="danger"
              icon={<FiAlertCircle />}
              title={`Não foi possível obter o status do Bloco ${nomeBlocoStatus || ''}`.trim()}
              text="Tente novamente. Se o problema continuar, entre em contato com o suporte."
              actionLabel="Tentar novamente"
              onAction={handleRefresh}
            />
          )}

          {statusPillVariant === 'partial' && (
            <InlineNotice
              tone="warning"
              icon={<FiAlertTriangle />}
              title="Atualização parcial"
              text="Alguns exaustores não foram consultados a tempo. Os cartões marcados exibem a última leitura conhecida."
              actionLabel="Atualizar"
              onAction={handleRefresh}
            />
          )}

          <div className="feature-section__body">
            {selectedBlocoStatus ? (
              <div className={`exaustores-grid ${exaustoresDoBlocoSelecionado.length % 2 === 0 ? 'grid-par' : ''}`}>
                {exaustoresDoBlocoSelecionado.map((grupo) => {
                  // Todos os finais de um mesmo exaustor compartilham o canal
                  // fisico, entao qualquer um deles reflete o mesmo status.
                  const statusKey = `final${grupo.finais[0]}`;

                  return (
                    <ExaustorStatusCard
                      key={`exaustor${grupo.exaustor}`}
                      exaustorLabel={grupo.exaustor}
                      finais={grupo.finais}
                      status={status[statusKey]}
                      remainingTime={remainingTimes[statusKey]}
                      hasError={statusFalhou}
                      isLoading={isLoadingStatus}
                      isStale={finaisSemLeitura.includes(statusKey)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="feature-section__empty">
                Selecione um bloco para ver o status dos exaustores.
              </div>
            )}
          </div>
        </section>

        {/* Section: Histórico */}
        <section className="feature-section">
          <HistoricoSection
            historico={historico}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onRefresh={handleRefreshHistorico}
            isSpinningHistorico={isSpinningHistorico}
          />
        </section>

        {/* FAB + Diálogo de comando */}
        <FloatingActionButton onClick={openComandoDialog} />

        <ComandoDialog
          open={showComandoDialog}
          phase={comandoPhase}
          onClose={
            comandoPhase === 'form' ? closeComandoDialog
              : comandoPhase === 'result' && !resultadoComando?.verificado ? closeResultado
              : undefined
          }
          blocos={blocos}
          apartamentos={apartamentos}
          selectedBloco={selectedBloco}
          selectedApto={selectedApto}
          selectedAction={selectedAction}
          userName={userName}
          onBlocoChange={handleBlocoChange}
          onAptoChange={setSelectedApto}
          onActionChange={setSelectedAction}
          onUserNameChange={setUserName}
          formError={formError}
          onSubmit={submitComando}
          exaustorNome={nomeDoExaustor(selectedBloco, selectedApto)}
          progress={sendProgress}
          resultado={resultadoComando}
          autoCloseMs={SUCESSO_AUTO_CLOSE_MS}
          onRetry={sendCommand}
          onBack={backToForm}
          onViewStatus={viewStatus}
        />
      </div>
    </AppLayout>
  );
};

export default App4;
